/**
 * dealerLedgerApi — the web side of the dealer credit ledger
 * (LOCKED SPEC 2026-09-23, plan Task 5).
 *
 * 🔒 EVERY amount on this wire is an INTEGER NUMBER OF PAISE and is named
 * `*Paise`. Nothing in this file hands a paise integer to a screen: `rupees()`
 * is the ONE place paise become rupees, and every page formats through it
 * (plan: "format once, in one helper, and never let paise reach the screen").
 *
 * 🔒 L10 one number everywhere — the list, the statement, the drawer block and
 * the convert warning all read the SAME server ledger. No screen computes a
 * balance of its own; the running balance on a statement line is the server's
 * `balancePaise`, not a client sum.
 *
 * Wire (backend plan Tasks 3 and 4):
 *   GET   /api/v1/dealer-ledger                 → { data: { dealers, asOf } }
 *   GET   /api/v1/dealer-ledger/:dealerId       → { data: DealerLedger & { dealerName } }
 *   PATCH /api/v1/dealer-ledger/:dealerId/terms → { creditLimitRupees?, creditDays? }
 *         🔒 `creditLimitRupees` is the ONE rupee field on this wire and says so
 *         in its name; everything else is `*Paise`. The server refuses a bare
 *         `creditLimit` with 400 CREDIT_LIMIT_FIELD_RENAMED rather than
 *         guessing the unit. No screen calls it yet.
 */
import { z } from 'zod';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

export type LedgerLineKind = 'order' | 'receipt';

export interface LedgerLine {
  kind: LedgerLineKind;
  at: string;
  ref: string;
  debitPaise: number;
  creditPaise: number;
  /** Running balance AFTER this line, counted oldest → newest. Server-computed. */
  balancePaise: number;
  ageDays?: number | null;
}

export interface DealerLedgerBuckets {
  current: number;
  days30: number;
  days60: number;
  over90: number;
}

export interface DealerLedger {
  dealerId: string;
  dealerName?: string | null;
  balancePaise: number;
  oldestUnpaidAgeDays: number | null;
  buckets: DealerLedgerBuckets;
  creditLimitPaise: number | null;
  creditDays: number | null;
  overLimit: boolean;
  overdue: boolean;
  /** Newest first, as the server returns them. */
  lines: LedgerLine[];
}

export interface DealerLedgerSummary {
  dealerId: string;
  name: string;
  balancePaise: number;
  oldestUnpaidAgeDays: number | null;
  overLimit: boolean;
  overdue: boolean;
  /**
   * The server DOES send both (`loadDealerLedgerSummaries`). Read as optional
   * anyway so the column degrades to "No limit set" rather than rendering
   * `undefined` if a row ever arrives without them.
   */
  creditLimitPaise?: number | null;
  creditDays?: number | null;
  /** Orders left out of this balance because their money could not be read. */
  needsReviewOrders?: number;
}

export interface DealerLedgerList {
  dealers: DealerLedgerSummary[];
  asOf: string | null;
  /** Total unreadable orders across the list. */
  needsReview: number;
}

/** The convert-time warning (backend Task 4). `null` = nothing to say. */
export interface CreditWarning {
  overLimit: boolean;
  overdue: boolean;
  balancePaise: number;
  creditLimitPaise: number | null;
  oldestUnpaidAgeDays: number | null;
}

// ── money ──────────────────────────────────────────────────────────────────

/**
 * Paise → rupees, as an exact decimal string. Integer arithmetic only: the
 * paise are split with `/` and `%` on the integer, never divided as a float.
 *
 * 🔒 HONEST ABOUT THE MARGIN. `(paise / 100).toFixed(2)` in fact agrees with
 * this on every value below 2^53 — ₹5,92,239.76 is safe either way — so this
 * is discipline, not a paisa already being lost. The two answers diverge above
 * 2^53 (see dealerLedgerApi.test.ts), and the discipline is what keeps the
 * rule "no float ever touches money" simple enough to hold everywhere.
 */
export function rupeesDecimal(paise: number | null | undefined): string {
  if (paise == null || !Number.isFinite(paise)) return '0.00';
  const rounded = Math.round(paise);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const whole = Math.floor(abs / 100);
  const part = abs % 100;
  return `${sign}${whole}.${String(part).padStart(2, '0')}`;
}

/** 🔒 The ONE formatter. A screen never sees a paise integer. */
export function rupees(paise: number | null | undefined): string {
  return formatINR(rupeesDecimal(paise));
}

/** Compact rupees for a chip: ₹2.4 L, ₹1.3 Cr, ₹8,400. */
export function rupeesCompact(paise: number | null | undefined): string {
  if (paise == null || !Number.isFinite(paise)) return '—';
  const abs = Math.abs(Math.round(paise));
  const sign = paise < 0 ? '-' : '';
  if (abs >= 10000000 * 100) return `${sign}₹${(abs / (10000000 * 100)).toFixed(1)} Cr`;
  if (abs >= 100000 * 100) return `${sign}₹${(abs / (100000 * 100)).toFixed(1)} L`;
  return formatINR(rupeesDecimal(paise), false);
}

// ── the chip, in plain words ───────────────────────────────────────────────

/**
 * The reasons a dealer is flagged, each one a sentence a human can read.
 * A colour alone is not a reason (plan Task 5).
 */
export function creditChipReasons(input: {
  overLimit: boolean;
  overdue: boolean;
  balancePaise: number;
  creditLimitPaise?: number | null;
  oldestUnpaidAgeDays?: number | null;
  creditDays?: number | null;
}): string[] {
  const reasons: string[] = [];
  if (input.overLimit) {
    const limit = input.creditLimitPaise;
    if (limit != null && Number.isFinite(limit)) {
      reasons.push(`${rupeesCompact(input.balancePaise - limit)} over limit`);
    } else {
      reasons.push('over credit limit');
    }
  }
  if (input.overdue) {
    const age = input.oldestUnpaidAgeDays;
    reasons.push(age != null ? `oldest bill ${age} days` : 'overdue');
  }
  return reasons;
}

/** "74 days" / "—" when a dealer has nothing unpaid. */
export function ageLabel(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '—';
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** The credit limit column / header value. Unset is the normal case today. */
export function creditLimitLabel(paise: number | null | undefined): string {
  if (paise == null || !Number.isFinite(paise)) return 'No limit set';
  return rupees(paise);
}

// ── parsing ────────────────────────────────────────────────────────────────

const paise = z.number().int().safe();
const nullablePaise = paise.nullable();
const nullableDays = z.number().int().safe().nullable();

const summarySchema = z.object({
  dealerId: z.string().min(1),
  name: z.string(),
  balancePaise: paise,
  oldestUnpaidAgeDays: nullableDays,
  overLimit: z.boolean(),
  overdue: z.boolean(),
  creditLimitPaise: nullablePaise.optional(),
  creditDays: nullableDays.optional(),
  needsReviewOrders: z.number().int().nonnegative().optional(),
});

const lineSchema = z.object({
  kind: z.enum(['order', 'receipt']),
  at: z.string(),
  ref: z.string(),
  debitPaise: paise,
  creditPaise: paise,
  balancePaise: paise,
  ageDays: nullableDays.optional(),
});

const ledgerSchema = z.object({
  dealerId: z.string().min(1),
  dealerName: z.string().nullable().optional(),
  balancePaise: paise,
  oldestUnpaidAgeDays: nullableDays,
  buckets: z.object({
    current: paise,
    days30: paise,
    days60: paise,
    over90: paise,
  }),
  creditLimitPaise: nullablePaise,
  creditDays: nullableDays,
  overLimit: z.boolean(),
  overdue: z.boolean(),
  lines: z.array(lineSchema),
});

const listSchema = z.object({
  dealers: z.array(summarySchema),
  asOf: z.string().nullable().optional(),
  needsReview: z.number().int().nonnegative().optional(),
});

const creditWarningSchema = z.object({
  overLimit: z.boolean(),
  overdue: z.boolean(),
  balancePaise: paise,
  creditLimitPaise: nullablePaise,
  oldestUnpaidAgeDays: nullableDays,
});

export function parseDealerLedgerList(raw: unknown): DealerLedgerList {
  const parsed = listSchema.safeParse(raw);
  // Dealer rows carry party names — keep the payload out of the error.
  if (!parsed.success) throw new Error('Invalid dealer ledger response');
  return { dealers: parsed.data.dealers, asOf: parsed.data.asOf ?? null, needsReview: parsed.data.needsReview ?? 0 };
}

export function parseDealerLedger(raw: unknown): DealerLedger {
  const parsed = ledgerSchema.safeParse(raw);
  if (!parsed.success) throw new Error('Invalid dealer ledger response');
  return parsed.data;
}

/**
 * Pull the convert-time warning off a convert response, whatever shape the
 * route wraps it in. A missing, null or malformed warning is simply "no
 * warning" — 🔒 L6, it may never stop a conversion that already succeeded.
 */
export function readCreditWarning(responseData: unknown): CreditWarning | null {
  // 🔒 The REAL level is `body.creditWarning` — a sibling of `data`, which is
  // what sales-orders.routes.ts sends. The `data.creditWarning` fallback is
  // tolerance for a surface that wraps its reply differently, and both levels
  // are tested (dealerLedgerApi.test.ts); neither is there by accident.
  const body = responseData as { data?: unknown; creditWarning?: unknown } | null | undefined;
  if (!body || typeof body !== 'object') return null;
  const inner = (body.data ?? null) as { creditWarning?: unknown } | null;
  const raw = body.creditWarning ?? (inner && typeof inner === 'object' ? inner.creditWarning : null);
  if (!raw) return null;
  const parsed = creditWarningSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

// ── requests ───────────────────────────────────────────────────────────────

export async function fetchDealerLedgerList(
  filters: { onlyOverdue?: boolean; onlyOverLimit?: boolean } = {},
  signal?: AbortSignal,
): Promise<DealerLedgerList> {
  // SUN-48 item 8: the server filters; the page sends the switches instead of filtering in the browser.
  const params: Record<string, number> = {};
  if (filters.onlyOverdue) params.onlyOverdue = 1;
  if (filters.onlyOverLimit) params.onlyOverLimit = 1;
  const res = await api.get('/dealer-ledger', { signal, params });
  return parseDealerLedgerList(res.data?.data ?? res.data);
}

export async function fetchDealerLedger(
  dealerId: string,
  signal?: AbortSignal,
): Promise<DealerLedger> {
  const res = await api.get(`/dealer-ledger/${dealerId}`, { signal });
  return parseDealerLedger(res.data?.data ?? res.data);
}
